import React from 'react';
import { DateTime } from 'luxon';
import {
  AdminLegendVariant,
  ICalendarEventType,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';
import ICalendar from '../../../types/ICalendar';
import { eventsMapper } from '../../Dashboard/MainCalendar/helpers';
import {
  adminBookedLegendObject,
  adminLegendObject,
  adminPostedLegend,
  adminRequestedLegend,
  adminTotalLegend,
  IAdminLegendItem,
} from './LegendContainer';
import AdminLegend from './AdminLegend';
import AdminBookedLegend from './AdminBookedLegend';
import { IAdminBookedLegendItem } from './AdminBookedLegend/AdminBookedLegendItem/AdminBookedLegendItem';
import genericClasses from '../../../theme/GenericClasses';

export const getTotalCount = (
  events: Array<ICalendar>,
  filterBy: `${ICalendarEventType}`,
  activeMonth: number,
) =>
  events?.reduce((acc, cur) => {
    const eventActiveMonth = new Date(cur.startDate).getMonth();
    if (cur.type === filterBy && eventActiveMonth === activeMonth) {
      // eslint-disable-next-line no-param-reassign
      acc += 1;
    }
    return acc;
  }, 0);

export const getTotalHrs = (
  events: Array<ICalendar>,
  hoursOf: `${ICalendarEventType}`,
  activeMonth: number,
) =>
  events?.reduce((acc, cur) => {
    const eventActiveMonth = new Date(cur.start).getMonth();
    if (cur.type === hoursOf && eventActiveMonth === activeMonth) {
      // eslint-disable-next-line no-param-reassign
      acc += Math.abs(
        DateTime.fromISO(cur.endDate)
          .diff(DateTime.fromISO(cur.startDate))
          .as('hour'),
      );
    }
    return acc;
  }, 0);

export enum AdminBookedLegendKey {
  bookedPharmacists = 'bookedPharmacists',
  bookedTechnicians = 'bookedTechnicians',
}

export type AdminLegendKey = `${AdminLegendVariant}` | `${AdminBookedLegendKey}`;

const bookedLegendReplacementTypes: Record<AdminBookedLegendKey, ProfessionalReplacementTypes> = {
  [AdminBookedLegendKey.bookedPharmacists]: ProfessionalReplacementTypes.pharmacist,
  [AdminBookedLegendKey.bookedTechnicians]: ProfessionalReplacementTypes.technician,
};

const bookedLegendLabelKeys: Record<AdminBookedLegendKey, IAdminBookedLegendItem['labelKey']> = {
  [AdminBookedLegendKey.bookedPharmacists]: 'pharm',
  [AdminBookedLegendKey.bookedTechnicians]: 'tech',
};

// BOOKED EVENTS ARE SPLIT INTO SEPARATE LEGEND ENTRIES PER PROFESSIONAL TYPE
const getAdminLegendKey = ({ type, replacementType }: ICalendar): AdminLegendKey => {
  if (type !== AdminLegendVariant.booked) {
    return type as AdminLegendKey;
  }

  return replacementType === ProfessionalReplacementTypes.technician
    ? AdminBookedLegendKey.bookedTechnicians
    : AdminBookedLegendKey.bookedPharmacists;
};

const getAdminLegendEvents = (
  events: Array<ICalendar>,
  legendKey: AdminLegendKey,
) =>
  events.filter((event) =>
    getAdminLegendKey(event) === legendKey);

const getAdminLegendCount = (
  events: Array<ICalendar>,
  { key, variant }: IAdminLegendItem,
  activeMonth: number,
) =>
  getTotalCount(getAdminLegendEvents(events, key), variant, activeMonth);

const getAdminLegendHours = (
  events: Array<ICalendar>,
  { key, variant }: IAdminLegendItem,
  activeMonth: number,
) =>
  getTotalHrs(
    getAdminLegendEvents(events, key).map((e) =>
      eventsMapper(e)),
    variant,
    activeMonth,
  );

export const adminTotal = (events: Array<ICalendar>, activeMonth: number) =>
  adminLegendObject.reduce(
    (acc, curr) => {
      acc.totalHours += getAdminLegendHours(events ?? [], curr, activeMonth);
      acc.totalCount += getAdminLegendCount(events ?? [], curr, activeMonth);
      return acc;
    },
    { totalHours: 0, totalCount: 0 },
  );

// BOOKED PHARMACISTS & TECHNICIANS SHARE A SINGLE LEGEND CHIP, EACH OPENING ITS OWN FILTERED REPORT
const getAdminBookedLegendItems = (
  events: ICalendar[],
  currentActiveMonth: number,
) =>
  adminBookedLegendObject.map((leg) => {
    const bookedKey = leg.key as AdminBookedLegendKey;

    return {
      labelKey: bookedLegendLabelKeys[bookedKey],
      replacementType: bookedLegendReplacementTypes[bookedKey],
      totalCount: getAdminLegendCount(events, leg, currentActiveMonth),
      totalHrs: getAdminLegendHours(events, leg, currentActiveMonth),
    };
  });

export const AdminRenderer = (
  totalCount: number,
  totalHours: number,
  events: ICalendar[],
  currentActiveMonth: number,
  row:boolean,
) => {
  const customClass = row ? genericClasses.legendsRowMargin : genericClasses.legendsMargin;

  const renderAdminLegend = (leg: IAdminLegendItem) => {
    const isTotal = leg.variant === AdminLegendVariant.total;
    const legendCount = isTotal
      ? totalCount
      : getAdminLegendCount(events, leg, currentActiveMonth);
    const legendHours = isTotal
      ? totalHours
      : Math.trunc(getAdminLegendHours(events, leg, currentActiveMonth));

    return (
      <AdminLegend
        totalCount={legendCount}
        totalHrs={legendHours}
        variant={leg.variant}
        isRow={row}
        customClass={customClass}
        key={leg.key}
      />
    );
  };

  return [
    renderAdminLegend(adminRequestedLegend),
    renderAdminLegend(adminPostedLegend),
    <AdminBookedLegend
      key={AdminLegendVariant.booked}
      items={getAdminBookedLegendItems(events, currentActiveMonth)}
      isRow={row}
      customClass={customClass}
    />,
    renderAdminLegend(adminTotalLegend),
  ];
};
